import { isInZeloolApp } from '@/utils/tools';

/** 页面离开事件的两类业务触发来源。 */
export type PageLeaveTrigger = 'pathname_change' | 'beforeunload';

/** 创建页面快照所需的最小浏览器数据源。 */
export interface PageLeaveSnapshotSource {
  location: {
    href: string;
    pathname: string;
    search: string;
  };
  document: {
    referrer: string;
    title: string;
  };
  now: () => number;
}

/** 进入页面时冻结的业务快照。 */
export interface PageLeaveSnapshot {
  enteredAt: number;
  pageUrl: string;
  urlPath: string;
  referrer: string;
  documentTitle: string;
  attempted: boolean;
}

/** 每个 Hook 实例持有的页面离开状态。 */
export interface PageLeaveState {
  snapshot: PageLeaveSnapshot | null;
}

/** PageLeave 普通业务事件属性。 */
export interface PageLeaveProperties extends Record<string, unknown> {
  page_url: string;
  url_path: string;
  referrer: string;
  document_title: string;
  view_duration: number;
  leave_trigger: PageLeaveTrigger;
  next_url_path?: string;
}

/** 判断当前运行时是否允许 Web 侧追踪页面离开。 */
export function canTrackPageLeave(
  options: {
    hasWindow?: boolean;
    inApp?: boolean;
  } = {},
): boolean {
  const hasWindow = options.hasWindow ?? typeof window !== 'undefined';

  // SSR 环境直接禁用，也避免继续读取 App 浏览器能力。
  if (!hasWindow) return false;

  const inApp = options.inApp ?? isInZeloolApp();
  return !inApp;
}

/** 冻结当前进入页的 URL、文档信息与开始时间。 */
export function createPageLeaveSnapshot(source: PageLeaveSnapshotSource): PageLeaveSnapshot {
  return {
    enteredAt: source.now(),
    pageUrl: source.location.href,
    urlPath: `${source.location.pathname}${source.location.search}`,
    referrer: source.document.referrer || '',
    documentTitle: source.document.title || '',
    attempted: false,
  };
}

/** 用当前页面快照重置一次性尝试状态。 */
export function resetPageLeaveState(state: PageLeaveState, source: PageLeaveSnapshotSource): void {
  state.snapshot = createPageLeaveSnapshot(source);
}

/** 基于进入页快照最多生成一次普通 PageLeave properties。 */
export function createPageLeavePropertiesOnce(
  state: PageLeaveState,
  trigger: PageLeaveTrigger,
  source: PageLeaveSnapshotSource,
  nextUrlPath?: string,
): PageLeaveProperties | null {
  const snapshot = state.snapshot;

  // 没有快照或本页已尝试时不再生成 properties。
  if (!snapshot || snapshot.attempted) return null;

  const viewDuration = Math.max(0, Math.ceil((source.now() - snapshot.enteredAt) / 1000));
  const properties: PageLeaveProperties = {
    page_url: snapshot.pageUrl,
    url_path: snapshot.urlPath,
    referrer: snapshot.referrer,
    document_title: snapshot.documentTitle,
    view_duration: viewDuration,
    leave_trigger: trigger,
  };

  // 只有 pathname 变化携带新页面 pathname，不附带 query。
  if (trigger === 'pathname_change' && nextUrlPath) {
    properties.next_url_path = nextUrlPath;
  }

  // 在外部发送前先标记尝试，确保 Client 缺失或抛错也不会重复尝试。
  state.snapshot = {
    ...snapshot,
    attempted: true,
  };

  return properties;
}
